<template>
  <div class="right-panel">
    <div class="row">
      <div class="col">
        <h4>{{ terms?.CommunityInstructorPlural }}</h4>
      </div>
      <div class="col text-right">
        <button class="btn btn-primary" @click="onAddClick">Create New Deliverer</button>
      </div>
    </div>

    <div class="d-flex flex-row content form">
      <div class="row mr-1 form-input">
        <label for="basic-url">Search by Name</label>
        <div class="input-group">
          <input type="text" class="form-control" placeholder="Enter first or last name"
            aria-label="Enter first or last name" @input="searchByNameDebounce" />
        </div>
      </div>
      <div class="row form-input">
        <label for="basic-url">Search by Email/User ID</label>
        <div class="input-group">
          <input type="text" class="form-control" placeholder="email@domain.com" aria-label="email@domain.com"
            @input="searchByEmailDebounce" />
        </div>
      </div>
    </div>

    <div class="table-wrapper">
      <EasyDataTable v-model:server-options="serverOptions" table-class-name="datatable" class="data-table"
        :headers="headers" :items="items" :server-items-length="totalCount" :loading="isLoadingDeliverer"
        buttons-pagination :alternating="true" must-sort>
        <template #item-firstName="item">
          {{ item.firstName }}
        </template>
        <template #item-lastName="item">
          {{ item.lastName }}
        </template>
        <template #item-category="item">
          {{ item.category }}
        </template>
        <template #item-email="item">
          {{ item.email }}
        </template>
        <template #item-isAllAbilities="item">
          {{ item.isAllAbilities ? 'Yes' : 'No' }}
        </template>
        <template #item-dateCreated="item">
          {{ moment(item.dateCreated).format('DD MMM YYYY') }}
        </template>
        <template #item-buttons="item">
          <button class="btn-edit" @click="() => editDeliverer(item)">
            <i class="bi bi-pencil-square"></i>
          </button>
          <button v-b-modal="'my-modal'" class="btn-delete" @click="() => deleteDeliverer(item)">
            <i class="bi bi-trash ms-1"></i>
          </button>
        </template>
      </EasyDataTable>
    </div>
  </div>
  <Modal title="Remove deliverer" ref="deleteModal">
    <template #body>Are you sure you want to remove
      <strong>{{ `${selectedItem?.firstName + ' ' + selectedItem?.lastName}` }}</strong>?</template>
    <template #footer>
      <button class="btn btn-danger" @click="onClickRemove">Remove</button>
    </template>
  </Modal>

  <Modal :title="editCreateTitle" ref="editCreateModal" class="delivererModal">
    <template #body>
      <div class="container">
        <div class="row">
          <div class="col col-12 col-md-6 form-input">
            <label for="basic-url">First Name</label>
            <div class="input-group">
              <input type="text" class="form-control" placeholder="Enter first name" aria-label="Enter first name"
                v-model="deliverer.firstName" />
              <span class="error" v-if="v$.firstName.$error">
                {{ 'First Name is required' }}
              </span>
            </div>
          </div>
          <div class="col col-12 col-md-6 form-input">
            <label for="basic-url">Last Name</label>
            <div class="input-group">
              <input type="text" class="form-control" placeholder="Enter last name" aria-label="Enter last name"
                v-model="deliverer.lastName" />
              <span class="error" v-if="v$.lastName.$error">
                {{ 'Last Name is required' }}
              </span>
            </div>
          </div>
          <div class="col col-12 col-md-6 form-input">
            <label for="basic-url">Email</label>
            <div class="input-group">
              <input type="text" class="form-control" placeholder="email@domain.com" aria-label="email@domain.com"
                v-model="deliverer.email" />
              <span class="error" v-if="v$.email.$error">
                {{ 'Email is required or Invalid' }}
              </span>
            </div>
          </div>
          <div class="col col-12 col-md-6 form-input">
            <label for="basic-url">Gender</label>
            <div class="input-group">
              <select class="form-select" v-model="deliverer.gender">
                <option value="M">Male</option>
                <option value="W">Female</option>
              </select>
              <span class="error" v-if="v$.gender.$error">
                {{ 'Gender is required' }}
              </span>
            </div>
          </div>
          <div class="btn-group-toggle mr-2 mt-2" style="width: 200px; display: inline" data-toggle="buttons">
            <label class="btn btn-secondary bg-light text-secondary active">
              <input type="checkbox" v-model="deliverer.isAllAbilities" />
              <span class="checklabel">&nbsp; Is {{ terms?.ParticipationAllAbilities }}</span>
            </label>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <button class="btn btn-primary" :disabled="saving || v$.$errors.length > 0" @click="saveDeliverer">
        Submit
      </button>
    </template>
  </Modal>
  <Notification ref="notificationMsg" colorScheme="text-bg-success">
    <template #body>Successfully removed
      <strong>{{ `${selectedItem?.firstName + ' ' + selectedItem?.lastName}` }}</strong></template>
  </Notification>
  <Notification ref="saveNotificationMessage" colorScheme="text-bg-success">
    <template #body>Successfully saved!</template>
  </Notification>
</template>

<script setup lang="ts">
import type { Item, ServerOptions } from 'vue3-easy-data-table'
import { ref, watch, reactive, onMounted, computed } from 'vue'
import { storeToRefs } from 'pinia'
import moment from 'moment'
import { useDelivererStore } from '@/stores/deliverer-store'
import { debounce } from 'lodash'
import Modal from '@/components/common/Modal.vue'
import Notification from '@/components/common/Notification.vue'
import type { IDelivererVm } from '../types/IDelivererVm'
import { useVuelidate } from '@vuelidate/core'
import { required, email } from '@vuelidate/validators'
import { useTermsStore } from '@/stores/language-terms'
const { terms } = storeToRefs(useTermsStore());

const { getDeliverers, removeDeliverer, createUpdateDeliverer, loadDelivererTableHeaders } =
  useDelivererStore()
const { deliverers, totalCount, isLoadingDeliverer, delivererTableHeaders } = storeToRefs(
  useDelivererStore()
)
const searchByName = ref()
const searchByEmail = ref()
const selectedItem = ref()
const saving = ref(false)
const editCreateTitle = ref('Create New Deliverer')
let deliverer = reactive({
  memberUID: null,
  delivererTypeId: 1,
  firstName: '',
  lastName: '',
  email: '',
  gender: '',
  isAllAbilities: false
})
let editCreateModal = ref<any | null>(null)
let deleteModal = ref<any | null>(null)
let notificationMsg = ref<any | null>(null)
let saveNotificationMessage = ref<any | null>(null)

const rules = computed(() => ({
  delivererTypeId: { required },
  firstName: { required }, // Matches state.firstName
  lastName: { required }, // Matches state.lastName
  email: { required, email }, // Matches state.contact.email
  gender: { required }
}))

const v$ = useVuelidate(rules, deliverer)

const searchByNameDebounce = debounce(($event) => {
  if ($event.target.value && $event.target.value.length <= 2) return
  searchByName.value = $event.target.value
}, 600)

const searchByEmailDebounce = debounce(($event) => {
  if ($event.target.value && $event.target.value.length <= 2) return
  searchByEmail.value = $event.target.value
}, 600)

const items = ref<Item[]>([])
const headers = delivererTableHeaders as any
const serverOptions = ref<ServerOptions>({
  page: 1,
  rowsPerPage: 20,
  sortBy: 'CreateDate',
  sortType: 'desc'
})

const loadData = async () => {
  await loadDelivererTableHeaders();
  await loadPrograms();
}

const loadPrograms = async () => {
  await getDeliverers(
    searchByName.value,
    searchByEmail.value,
    serverOptions.value.page,
    serverOptions.value.rowsPerPage,
    serverOptions.value.sortBy,
    serverOptions.value.sortType
  )
  items.value = deliverers.value
}

const deleteDeliverer = (item: any) => {
  selectedItem.value = Object.assign({}, item)
  if (deleteModal.value) {
    deleteModal.value.show()
  }
}

const editDeliverer = (item: IDelivererVm) => {
  v$.value.$reset()
  editCreateTitle.value = 'Edit Deliverer'
  deliverer = Object.assign(deliverer, item)
  editCreateModal.value.show()
}

const saveDeliverer = async () => {
  v$.value.$reset()
  const isValid = await v$.value.$validate()
  if (isValid) {
    saving.value = true
    try {
      deliverer.delivererTypeId = 1
      const result = await createUpdateDeliverer(deliverer)
      if (result) {
        await loadData()
      }
    } catch (error) {
      console.log(error)
    } finally {
      saving.value = false
      editCreateModal.value.hide()
      saveNotificationMessage?.value?.show()
    }
  }
}

const onClickRemove = async () => {
  const result = await removeDeliverer(selectedItem.value.memberUID)
  if (result) {
    deleteModal?.value?.hide()
    notificationMsg?.value?.show()
    await loadPrograms()
  }
}

const onAddClick = () => {
  editCreateTitle.value = 'Create New Deliverer'
  v$.value.$reset()
  deliverer = Object.assign(deliverer, {
    memberUID: null,
    firstName: '',
    lastName: '',
    email: '',
    delivererTypeId: 1,
    gender: ''
  })
  editCreateModal.value.show()
}

onMounted(async () => {
  await loadData()
})

watch([searchByEmail, searchByName, serverOptions], async () => {
  await loadData()
})
</script>

<style scoped>
.mr-1 {
  margin-right: 10px !important;
}

label.form-label {
  font-size: 13px;
}

.delivererModal .form-input label {
  padding-bottom: 5px;
  margin-bottom: 0;
}

.delivererModal .form-input {
  margin-bottom: 10px;
}

.text-right {
  text-align: right;
}

.error {
  display: block;
  width: 100%;
  color: red;
  font-size: 14px;
  margin-top: 5px;
}
</style>
